import { YearMonth } from 'appTypes';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { posMod } from 'features/common/utils';

dayjs.extend(customParseFormat);

export function parseDate(YYYY_MM_DD: string): dayjs.Dayjs {
  return dayjs(YYYY_MM_DD, 'YYYY-MM-DD');
}
export function dateToString(date: dayjs.Dayjs, format = 'YYYY-MM-DD'): string {
  return date.format(format);
}
// generates a list of YYYY-MM-DD strings from start through end (inclusive)
export function daysInRange(start: string, end?: string): string[] {
  if (!start) return [];
  let from = parseDate(start);
  let to = end ? parseDate(end) : from;
  if (to.isBefore(from)) {
    const earlier = to;
    to = from;
    from = earlier;
  }
  const dates: string[] = [];
  let current = from;
  while (current.isBefore(to, 'day') || current.isSame(to, 'day')) {
    dates.push(dateToString(current));
    current = current.add(1, 'day');
  }
  return dates;
}
export function isValidDateTimeFormat(input: string): boolean {
  return dayjs(input, 'YYYY-MM-DDTHH:mm:ss', true).isValid();
}
export function parseDateTimeIgnoringTz(date: string): dayjs.Dayjs {
  return dayjs(date, 'YYYY-MM-DDTHH:mm:ss');
}

export function parseZonedDateTime(date: string): dayjs.Dayjs {
  return dayjs(date, 'YYYY-MM-DDTHH:mm:ssZ');
}

export const dateTimeToString = (date: dayjs.Dayjs): string => {
  return date.format('YYYY-MM-DDTHH:mm:ss');
};

/**
 *
 * @param date
 * @returns formatted string e.g., 31 October, 2025
 */
export function displayFriendlyDate(YYYY_MM_DD: string | Dayjs): string {
  // convert ISO to USA format: YYYY-MM-DD -> MM/DD/YYYY
  return dayjs(YYYY_MM_DD, 'YYYY-MM-DD').format('MMMM DD YYYY');
}

export const formatDateTimeWithAt = (date: Dayjs): string => {
  return date.format('D MMMM, YYYY [at] hh:mm A');
};

export function displayFriendlyDayMonthYear(YYYY_MM_DD: string): string {
  // convert ISO to USA format: YYYY-MM-DD -> FULLMONTH DD, YYYY
  return parseDate(YYYY_MM_DD).format('MMMM DD, YYYY');
}

export function displayFriendlyDateWithTime(date: string): string {
  return parseDateTimeIgnoringTz(date).format('MMM D, YYYY h:mm A');
}

export function parseMonthYear(date: string): dayjs.Dayjs {
  return dayjs(date, 'MMMM-YYYY');
}

export function displayTypicalMonth(MMMM_YYYY: string): string {
  return dayjs(MMMM_YYYY, 'MMMM-YYYY').format('MMMM YYYY');
}

export function isOnDay(ts: number, date: string) {
  return dayjs(ts).isSame(dayjs(date), 'day');
}

export function isEndAfterStart(startDate: string, endDate: string) {
  return dayjs(endDate).valueOf() > dayjs(startDate).valueOf();
}

export function formatTimeOfDay(value: number): string {
  const hours = posMod(Math.floor(value / 60), 24);
  const minutes = posMod(value, 60);

  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Checks whether a day is within a (start, end] range.
 * Start is exclusive, end is inclusive.
 */
export function isInRange(
  day: dayjs.Dayjs,
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
) {
  if (!startDate || !endDate) return false;
  const time = dayjs(day).startOf('day');
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  return time.isAfter(start) && (time.isBefore(end) || time.isSame(end));
}

export function formatTimeOfDay12HrClock(value: number): string {
  let hours = posMod(Math.floor(value / 60), 24);
  let ampm = 'AM';
  if (hours >= 12) {
    ampm = 'PM';
  }
  if (hours > 12) {
    hours -= 12;
  }
  if (hours === 0) {
    hours = 12;
  }

  const minutes = posMod(value, 60);

  return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

export function nowUnix(): number {
  return Date.now();
}

export function displayComparisonMonth(YYYY_MM: string) {
  return dayjs(YYYY_MM, 'YYYY-MM').format('MMMM YYYY');
}

export function getLast24Months(): YearMonth[] {
  const months: YearMonth[] = [];
  const today = dayjs();

  for (let i = 0; i < 24; i++)
    months.push(today.subtract(i, 'month').format('YYYY-MM') as YearMonth);

  return months as YearMonth[];
}

export const formatTime = (v: number) => {
  if (v || v === 0) {
    const d = dayjs.duration(v, 's');
    return d.format('HH:mm:ss');
  } else {
    return '';
  }
};

export function validateDateRange(
  rangeStart?: string | null,
  rangeEnd?: string | null,
): boolean {
  if (!rangeStart || !rangeEnd) return false;
  if (!dayjs(rangeEnd).isAfter(dayjs(rangeStart))) return false;
  return true;
}

export const monthToName = (month: string): string =>
  dayjs(`2000-${month}-01`).format('MMM');

export const isFutureDate = (date: string | Dayjs) => {
  return dayjs(date).isAfter(dayjs());
};

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number];
export const HOURS_IN_DAY = 24;
export const MIN_IN_HOUR = 60;

/** Formats fractional hours (e.g. 3.5) as '3 h 30 min' */
export const formatHoursToHourMin = (hours: number): string => {
  const mins = hours * MIN_IN_HOUR;
  const h = Math.floor(mins / MIN_IN_HOUR);
  const min = mins % MIN_IN_HOUR;

  if (min === 0) return `${h} h`;
  if (h === 0) return `${min} min`;

  return `${h} h ${min} min`;
};

export function formatDateOrDateRange(
  targetDate: string,
  endDate?: string,
  template = 'D MMM, YYYY',
): string {
  if (endDate) {
    return `${dayjs(targetDate).format(template)} - ${dayjs(endDate).format(template)}`;
  }
  return dayjs(targetDate).format(template);
}
